import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/base/Seo';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

interface Chapter {
  num: number;
  title: string;
}

const pageImages = [
  'https://readdy.ai/api/search-image?query=manga%20comic%20panel%20page%20with%20dramatic%20action%20scene%20and%20speech%20bubbles%2C%20anime%20style%20inking%20with%20warm%20orange%20tones%2C%20dynamic%20layout%2C%20high%20contrast%20linework%2C%20clean%20detailed%20illustration&width=900&height=600&seq=page-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=manga%20comic%20panel%20page%20with%20emotional%20character%20dialogue%20scene%20and%20speech%20bubbles%2C%20anime%20style%20inking%20with%20warm%20amber%20tones%2C%20dynamic%20layout%2C%20high%20contrast%20linework%2C%20clean%20detailed%20illustration&width=900&height=600&seq=page-02&orientation=landscape',
  'https://readdy.ai/api/search-image?query=manga%20comic%20panel%20page%20with%20epic%20climax%20battle%20scene%20and%20speed%20lines%2C%20anime%20style%20inking%20with%20warm%20gold%20tones%2C%20dynamic%20layout%2C%20high%20contrast%20linework%2C%20clean%20detailed%20illustration&width=900&height=600&seq=page-03&orientation=landscape',
];

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);
  const [showAll, setShowAll] = useState(false);
  const [readerChapter, setReaderChapter] = useState<Chapter | null>(null);
  const [favorited, setFavorited] = useState(false);

  useEffect(() => {
    setShowAll(false);
    setReaderChapter(null);
    setFavorited(false);
  }, [id]);

  const chapters = useMemo<Chapter[]>(() => {
    if (!comic) return [];
    const arr: Chapter[] = [];
    for (let n = comic.chapterCount; n >= 1; n -= 1) {
      const isFinal = n === comic.chapterCount && comic.latest.startsWith('大结局');
      arr.push({ num: n, title: isFinal ? comic.latest : `第${n}话` });
    }
    return arr;
  }, [comic]);

  const visibleChapters = showAll ? chapters : chapters.slice(0, 24);
  const related = useMemo(() => {
    if (!comic) return [];
    const shareTag = comic.tags[0];
    return comics.filter((c) => c.id !== comic.id && c.tags.includes(shareTag)).slice(0, 5);
  }, [comic]);
  const fallbackRelated = related.length > 0 ? related : comics.slice(0, 5);

  if (!comic) {
    return (
      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-36 pb-24 text-center">
        <p className="text-foreground-500">未找到该漫画</p>
        <Link to="/comics" className="mt-4 inline-block text-primary-500">
          返回漫画大全
        </Link>
      </div>
    );
  }

  const activeChapterIndex = readerChapter
    ? chapters.findIndex((c) => c.num === readerChapter.num)
    : -1;

  return (
    <>
      <Seo
        title={`${comic.title} - ${comic.author}原版漫画全集高清在线看|麻瓜漫画网`}
        description={`${comic.title}（${comic.author}）完整版在线看，麻瓜漫画网提供该作高清原版与导演剪辑版全集，共${comic.chapterCount}话，${comic.latest}已更新，支持完整阅读。`}
        keywords={`麻瓜漫画网,${comic.title},${comic.author},原版漫画,完整版漫画,高清在线看`}
        canonical={`https://uncutm.github.io/comic/${comic.id}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ComicStory',
          name: comic.title,
          author: { '@type': 'Person', name: comic.author },
          description: comic.description,
          image: comic.cover,
          inLanguage: 'zh-CN',
          isPartOf: { '@type': 'WebSite', name: '麻瓜漫画网', url: 'https://uncutm.github.io/' },
        }}
      />

      <div className="mx-auto max-w-7xl px-4 md:px-6 pt-28 md:pt-36 pb-16">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500">
          <Link to="/" className="hover:text-primary-500">首页</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/comics" className="hover:text-primary-500">漫画大全</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-950">{comic.title}</span>
        </nav>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
          {/* cover */}
          <div className="mx-auto w-48 md:w-full">
            <div className="relative aspect-[3/4] overflow-hidden rounded-xl ring-1 ring-background-300/50">
              <img
                src={comic.cover}
                alt={`${comic.title} 原版漫画封面`}
                title={comic.title}
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute left-2 top-2 flex flex-col gap-1">
                {comic.hot && (
                  <span className="flex items-center gap-1 rounded-full bg-primary-500 px-2 py-0.5 text-[10px] font-bold text-background-50 whitespace-nowrap">
                    <i className="ri-fire-fill"></i> 热
                  </span>
                )}
                <span className="rounded-full bg-secondary-500/90 px-2 py-0.5 text-[10px] font-bold text-background-50 whitespace-nowrap">
                  {comic.status}
                </span>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setReaderChapter(chapters[0]);
                  document.getElementById('chapters')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-full bg-primary-500 text-sm font-semibold text-background-50 hover:bg-primary-600 whitespace-nowrap"
              >
                <i className="ri-play-fill"></i> 开始阅读
              </button>
              <button
                type="button"
                onClick={() => setFavorited((v) => !v)}
                className={`flex h-11 w-12 items-center justify-center rounded-full text-lg transition-colors ${
                  favorited ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-primary-500'
                }`}
                aria-label="收藏"
              >
                <i className={favorited ? 'ri-heart-fill' : 'ri-heart-line'}></i>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setFavorited((v) => !v)}
              className="mt-2 h-10 w-full rounded-full bg-background-100 text-sm font-semibold text-foreground-700 hover:text-primary-500 whitespace-nowrap"
            >
              <i className="ri-bookmark-line mr-1"></i>
              {favorited ? '已收藏' : '加入书架'}
            </button>
          </div>

          {/* info */}
          <div>
            <div className="flex flex-wrap items-center gap-2">
              {comic.tags.map((t) => (
                <span key={t} className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900 whitespace-nowrap">
                  {t}
                </span>
              ))}
              <span className="flex items-center gap-1 rounded-md bg-accent-500/15 px-2 py-0.5 text-xs font-bold text-accent-600 whitespace-nowrap">
                <i className="ri-star-fill"></i> {comic.rating}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">{comic.title}</h1>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: '作者', value: comic.author, icon: 'ri-user-line' },
                { label: '状态', value: comic.status, icon: 'ri-flag-line' },
                { label: '总话数', value: `${comic.chapterCount} 话`, icon: 'ri-list-unordered' },
                { label: '人气', value: comic.views, icon: 'ri-eye-line' },
              ].map((s) => (
                <div key={s.label} className="flex items-center gap-3 rounded-lg bg-background-100 p-3">
                  <i className={`${s.icon} text-lg text-primary-500`}></i>
                  <div className="min-w-0">
                    <p className="text-xs text-foreground-500">{s.label}</p>
                    <p className="truncate text-sm font-semibold text-foreground-950">{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-5 rounded-xl bg-background-100 p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-file-text-line text-primary-500"></i> 作品简介
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">{comic.description}</p>
            </div>

            <p className="mt-4 text-xs text-foreground-500">
              更新于 {comic.updatedAt} · 最新：<span className="text-primary-500">{comic.latest}</span>
            </p>
          </div>
        </div>

        {/* chapters */}
        <div id="chapters" className="mt-12 scroll-mt-24">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">章节列表</h2>
            <span className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-500 whitespace-nowrap">
              共 {comic.chapterCount} 话
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {visibleChapters.map((ch, i) => {
              const isActive = readerChapter?.num === ch.num;
              return (
                <button
                  key={ch.num}
                  type="button"
                  onClick={() => setReaderChapter(ch)}
                  className={`truncate rounded-lg px-4 py-3 text-left text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-500/15 hover:text-primary-500'
                  }`}
                >
                  {ch.title}
                </button>
              );
            })}
          </div>
          {chapters.length > 24 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-background-100 text-sm font-semibold text-foreground-700 hover:text-primary-500 whitespace-nowrap"
            >
              {showAll ? '收起章节' : `展开全部 ${comic.chapterCount} 话`}
              <i className={`ri-arrow-down-s-line ${showAll ? 'rotate-180' : ''}`}></i>
            </button>
          )}
        </div>

        {/* related */}
        <div className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950">相关推荐</h2>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {fallbackRelated.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </div>
      </div>

      {/* reader modal */}
      {readerChapter && (
        <ReaderModal
          comicTitle={comic.title}
          chapter={readerChapter}
          total={chapters.length}
          onClose={() => setReaderChapter(null)}
          onPrev={() => {
            const next = chapters[activeChapterIndex + 1];
            if (next) setReaderChapter(next);
          }}
          onNext={() => {
            const prev = chapters[activeChapterIndex - 1];
            if (prev) setReaderChapter(prev);
          }}
          hasPrev={activeChapterIndex + 1 < chapters.length}
          hasNext={activeChapterIndex - 1 >= 0}
        />
      )}
    </>
  );
}

interface ReaderModalProps {
  comicTitle: string;
  chapter: Chapter;
  total: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

function ReaderModal({ comicTitle, chapter, total, onClose, onPrev, onNext, hasPrev, hasNext }: ReaderModalProps) {
  const [fontMode, setFontMode] = useState<'width' | 'fit'>('width');

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background-50">
      {/* reader header */}
      <div className="flex items-center justify-between gap-3 border-b border-background-200/70 bg-background-100/90 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:text-primary-500"
          aria-label="关闭阅读"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div className="min-w-0 flex-1 text-center">
          <p className="truncate text-sm font-bold text-foreground-950">
            {comicTitle} · {chapter.title}
          </p>
          <p className="text-xs text-foreground-500">
            {chapter.num} / {total}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFontMode((m) => (m === 'width' ? 'fit' : 'width'))}
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:text-primary-500"
            aria-label="切换显示模式"
          >
            <i className="ri-fullscreen-line text-lg"></i>
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-full text-foreground-700 hover:text-primary-500"
            aria-label="关闭"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>
      </div>

      {/* pages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className={`mx-auto flex flex-col gap-4 ${fontMode === 'width' ? 'max-w-4xl' : 'max-w-2xl'}`}>
          {pageImages.map((src, i) => (
            <div key={i} className="overflow-hidden rounded-lg ring-1 ring-background-300/40">
              <img src={src} alt={`${comicTitle} ${chapter.title} 第${i + 1}页`} className="w-full object-cover" />
            </div>
          ))}
          <p className="py-4 text-center text-sm text-foreground-500">—— 本章完，更多内容敬请期待 ——</p>
        </div>
      </div>

      {/* reader footer nav */}
      <div className="flex items-center justify-between gap-3 border-t border-background-200/70 bg-background-100/90 px-4 py-3">
        <button
          type="button"
          onClick={onPrev}
          disabled={!hasPrev}
          className="flex h-11 items-center gap-2 rounded-full bg-background-200 px-5 text-sm font-semibold text-foreground-700 hover:text-primary-500 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap"
        >
          <i className="ri-arrow-up-line"></i> 上一话
        </button>
        <span className="text-xs text-foreground-500 hidden sm:block">{chapter.title}</span>
        <button
          type="button"
          onClick={onNext}
          disabled={!hasNext}
          className="flex h-11 items-center gap-2 rounded-full bg-primary-500 px-5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 whitespace-nowrap"
        >
          下一话 <i className="ri-arrow-down-line"></i>
        </button>
      </div>
    </div>
  );
}